import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { listDraftChanges } from "../../api/drafts-v2";
import type { DocxChange } from "../../api/types/draft";
import { useOpenDraft, useOpenDraftTick } from "../draftEditor/openDrafts";

import { toDocxChange } from "./liveChanges";

type Options = {
  draftId: string;
  /** Re-fetch when this changes (the pane's reloadKey). */
  refreshKey?: unknown;
  /** Skip fetching until true. */
  enabled?: boolean;
};

/**
 * What is pending review in one draft: the tracked changes and their count.
 *
 * Read-only on purpose. Accepting and rejecting is the attorney's act, done on
 * the editor's own change cards (per change, in the page, undoable); nothing
 * here, and nothing the agent does, resolves a change. The hook exists so the
 * pane can show a count and notice when new changes arrive.
 *
 * Two sources, chosen per render by whether the draft is open in an editor
 * with a document model:
 *
 * - Open in the eigenpal editor: the list is read from the document model on
 *   every editor tick, so it is current the moment a change is proposed.
 * - Otherwise (not open, or the legacy engine): `GET /api/draft/:id/changes`,
 *   re-read on `refreshKey`.
 */
export const useDraftReview = ({
  draftId,
  refreshKey,
  enabled = true
}: Options) => {
  const [pending, setPending] = useState<DocxChange[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const openDraft = useOpenDraft(draftId);
  const tick = useOpenDraftTick(openDraft);
  const liveReview = openDraft?.surface()?.review ?? null;
  const live = Boolean(liveReview && !openDraft?.readOnly);

  // The live list: recomputed on every editor tick.
  const liveChanges = useMemo<DocxChange[] | null>(() => {
    if (!live || !liveReview) return null;
    try {
      return liveReview.listChanges().map(toDocxChange);
    } catch (e) {
      Sentry.captureException(e);
      return null;
    }
    // `tick` is the signal; the list itself is read from the editor.
  }, [live, liveReview, tick]);

  const refresh = useCallback(async () => {
    if (live) return;
    setLoading(true);
    try {
      const result = await listDraftChanges(draftId);
      setPending(result.changes);
      setError(null);
    } catch (e) {
      Sentry.captureException(e);
      setError("Couldn't load the pending changes.");
    } finally {
      setLoading(false);
    }
  }, [draftId, live]);

  useEffect(() => {
    if (!enabled || live) return;
    void refresh();
  }, [enabled, live, refresh, refreshKey]);

  const openChanges = useMemo<DocxChange[]>(
    () => (live ? liveChanges : pending) ?? [],
    [live, liveChanges, pending]
  );
  const known = live ? liveChanges !== null : pending !== null;

  return {
    /** The changes awaiting a decision, in document order. */
    openChanges,
    /** Pending count (live from the editor, else the server's); null until known. */
    pendingCount: known ? openChanges.length : null,
    /** Whether the list comes from the open editor rather than the server. */
    live,
    loading,
    error,
    refresh
  };
};

export type DraftReview = ReturnType<typeof useDraftReview>;
